import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentService } from '../services/paymentService';
import { PaymentRecord, PaymentStatus } from '../types/api';
import { format } from 'date-fns';
import { ArrowLeft, Download, AlertCircle } from 'lucide-react';
import useBottomNavPadding from '../hooks/useBottomNavPadding';
import { useTranslation } from 'react-i18next';

export default function BillingHistory() {
  const navigate = useNavigate();
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        setLoading(true);
        const payments = await paymentService.getPaymentHistory();
        console.log('Payments received:', payments);
        if (payments && Array.isArray(payments) && payments.length > 0) {
          console.log('Setting payment history with data');
          setPaymentHistory(payments);
        } else {
          console.log('No payments data or empty array', payments);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching payment history:', err);
        setError('Failed to load payment history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentHistory();
  }, []);

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Helper function to get status color class
  const getStatusClasses = (status: PaymentStatus) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return 'Invalid date';
      }
      return format(date, 'MMMM d, yyyy');
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  // Apply bottom padding for the navigation
  useBottomNavPadding();

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white p-4 border-b border-gray-200 flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-3"
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold">{t('billingHistory.pageTitle')}</h1>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('billingHistory.loading')}</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('billingHistory.errorTitle')}</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#F9D348] text-black font-semibold py-3 px-6 rounded-full"
            >
              {t('billingHistory.tryAgain')}
            </button>
          </div>
        ) : !paymentHistory || paymentHistory.length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-center">
            <h2 className="text-xl font-bold mb-2">{t('billingHistory.noHistoryTitle')}</h2>
            <p className="text-gray-600 mb-6">{t('billingHistory.noRecords')}</p>
            <button
              onClick={() => navigate('/subscription-management')}
              className="bg-[#F9D348] text-black font-semibold py-3 px-6 rounded-full"
            >
              {t('billingHistory.backToSubscription')}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">{t('billingHistory.sectionTitle')}</h2>
            </div>
            
            <div className="divide-y divide-gray-200">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2">
                    <span className="font-medium">{formatDate(payment.date)}</span>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium mt-1 sm:mt-0 ${getStatusClasses(payment.status)}`}>
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm">
                    <div className="text-gray-600">
                      {payment.subscriptionPeriod?.start && payment.subscriptionPeriod?.end ? (
                        <span>
                          {t('billingHistory.periodLabel')}{formatDate(payment.subscriptionPeriod.start)} - {formatDate(payment.subscriptionPeriod.end)}
                        </span>
                      ) : (
                        <span>{t('billingHistory.oneTimePayment')}</span>
                      )}
                    </div>
                    <div className="font-semibold mt-1 sm:mt-0">
                      {formatCurrency(payment.amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
} 